import { useMemo, useState, type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import { useHubChromeEnabled } from 'utils/featureFlags';
import { LayerListItem } from './LayerListItem';
import { useUscgMapContext } from './UscgMapContext';
import {
  filterMapLayerCatalog,
  groupMapLayerCatalog,
  type MapLayerCatalogItem,
} from './utils/mapLayerCatalog';

type PillTab = 'on-map' | 'all';

/**
 * Incident-map overlay pill (thehub HubMapVisibleItemsPill pattern). "On map"
 * lists visible layers with remove and clear all; "All layers" lists every
 * layer in scope using the Map Layers tab's own rows.
 */
export function MapVisibleLayersPill() {
  const {
    availableMapLayerItems,
    toggleExternalLayerVisibility,
    toggleInternalLayerVisibility,
    isArcGISAuthorized,
  } = useUscgMapContext();
  const hubChrome = useHubChromeEnabled();

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<PillTab>('on-map');
  const [searchQuery, setSearchQuery] = useState('');

  const toggle = (item: MapLayerCatalogItem) =>
    item.kind === 'External'
      ? toggleExternalLayerVisibility(item.id)
      : toggleInternalLayerVisibility(item.id);

  const visibleItems = useMemo(
    () => availableMapLayerItems.filter((item) => item.visible),
    [availableMapLayerItems]
  );
  const filteredVisible = useMemo(
    () => filterMapLayerCatalog(visibleItems, searchQuery),
    [visibleItems, searchQuery]
  );
  const filteredGroups = useMemo(
    () =>
      groupMapLayerCatalog(
        filterMapLayerCatalog(availableMapLayerItems, searchQuery)
      ),
    [availableMapLayerItems, searchQuery]
  );

  if (availableMapLayerItems.length === 0) {
    return null;
  }

  const visibleCount = visibleItems.length;
  const searching = searchQuery.trim().length > 0;
  const emptyText = searching
    ? 'No layers match your search.'
    : 'No layers available.';

  const clearAll = () => {
    for (const item of visibleItems) {
      toggle(item);
    }
    setOpen(false);
    setSearchQuery('');
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setTab(visibleCount > 0 ? 'on-map' : 'all');
    } else {
      setSearchQuery('');
    }
  };

  const showOrgSignInNote = !isArcGISAuthorized && !searching;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            size="sm"
            variant="rounded"
            id="map-visible-layers-pill"
            aria-label={
              visibleCount > 0
                ? `${visibleCount} map layer${
                    visibleCount === 1 ? '' : 's'
                  } visible. Open to manage.`
                : 'Map layers. Open to show layers on the map.'
            }
          />
        }
      >
        <SharedIcon iconName="stacks" size="sm" />
        {visibleCount > 0 ? `${visibleCount} on map` : 'Layers'}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="dark tw:w-[28rem] tw:p-0 tw:text-foreground"
      >
        <Tabs
          value={tab}
          onValueChange={(next) => setTab(next as PillTab)}
          className="tw:gap-0"
        >
          <div className="tw:flex tw:flex-col tw:gap-2 tw:border-b tw:border-border tw:px-3 tw:py-2">
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
              <span className="tw:text-sm tw:font-medium">Map layers</span>
              <Badge variant="secondary">{visibleCount} visible</Badge>
            </div>
            <TabsList className="tw:w-full">
              <TabsTrigger value="on-map">On map ({visibleCount})</TabsTrigger>
              <TabsTrigger value="all">
                All layers ({availableMapLayerItems.length})
              </TabsTrigger>
            </TabsList>
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search layers"
              aria-label="Search layers"
            />
          </div>

          <TabsContent value="on-map">
            <div className="tw:max-h-[28rem] tw:overflow-y-auto tw:px-2 tw:py-2">
              {filteredVisible.length === 0 ? (
                <p className="tw:mb-0 tw:py-4 tw:text-center tw:text-sm tw:text-muted-foreground">
                  {searching
                    ? emptyText
                    : 'No layers on the map. Turn some on in All layers.'}
                </p>
              ) : (
                <ul className="tw:mb-0 tw:list-none tw:p-0">
                  {filteredVisible.map((item) => (
                    <li
                      key={`${item.kind}-${item.id}`}
                      className="tw:flex tw:items-start tw:gap-2 tw:rounded tw:px-2 tw:py-2"
                    >
                      <div className="tw:min-w-0 tw:flex-1">
                        <div className="tw:mb-1 tw:flex tw:flex-wrap tw:items-center tw:gap-1">
                          <Badge variant="secondary">{item.kind}</Badge>
                        </div>
                        <div
                          className="tw:truncate tw:text-sm"
                          title={item.name}
                        >
                          {item.name}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove ${item.name} from map`}
                        onClick={() => toggle(item)}
                      >
                        <SharedIcon iconName="close" size="sm" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {visibleCount > 0 ? (
              <div className="tw:border-t tw:border-border tw:px-3 tw:py-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="tw:w-full"
                  onClick={clearAll}
                >
                  Clear all layers
                </Button>
              </div>
            ) : null}
          </TabsContent>

          <TabsContent value="all">
            <div className="tw:max-h-[28rem] tw:overflow-y-auto tw:px-2 tw:py-2">
              {filteredGroups.length === 0 && !showOrgSignInNote ? (
                <p className="tw:mb-0 tw:py-4 tw:text-center tw:text-sm tw:text-muted-foreground">
                  {emptyText}
                </p>
              ) : null}
              {showOrgSignInNote ? (
                <section aria-label="Organization" className="tw:mb-2">
                  <GroupHeading>Organization</GroupHeading>
                  <p className="tw:mb-0 tw:px-2 tw:text-xs tw:text-muted-foreground">
                    Sign in to ArcGIS in the Layers tab to see organization
                    layers.
                  </p>
                </section>
              ) : null}
              {filteredGroups.map((group) => {
                const rows = group.items.map((item) => {
                  const key = `${item.kind}-${item.id}`;
                  if (item.kind === 'Internal') {
                    return (
                      <LayerListItem
                        key={key}
                        item={{ type: 'internal', layer: item.layer }}
                      />
                    );
                  }
                  if (item.source !== 'preview') {
                    return (
                      <LayerListItem
                        key={key}
                        item={{
                          type: 'external',
                          layer: item.layer as ExternalLayer,
                        }}
                      />
                    );
                  }
                  return (
                    <PreviewLayerRow
                      key={key}
                      as={hubChrome ? 'div' : 'li'}
                      item={item}
                      onToggle={() => toggle(item)}
                    />
                  );
                });
                return (
                  <section
                    key={group.source}
                    aria-label={group.label}
                    className="tw:mb-3"
                  >
                    <GroupHeading>{group.label}</GroupHeading>
                    {hubChrome ? (
                      <div className="tw:space-y-2">{rows}</div>
                    ) : (
                      <ul className="list-unstyled d-grid mb-0 gap-2">
                        {rows}
                      </ul>
                    )}
                  </section>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}

/** Preview layers aren't saved layers, so they have no Map Layers tab row. */
function PreviewLayerRow({
  as: Row,
  item,
  onToggle,
}: {
  as: 'li' | 'div';
  item: MapLayerCatalogItem;
  onToggle: () => void;
}) {
  return (
    <Row className="tw:flex tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1.5">
      <div className="tw:min-w-0 tw:flex-1">
        <div className="tw:truncate tw:text-sm" title={item.name}>
          {item.name}
        </div>
        {item.loadError ? (
          <div className="tw:text-xs tw:text-destructive">Failed to load</div>
        ) : null}
      </div>
      <Switch
        checked={item.visible}
        disabled={item.loadError && !item.visible}
        aria-label={`${item.visible ? 'Hide' : 'Show'} ${item.name} on map`}
        onCheckedChange={onToggle}
      />
    </Row>
  );
}

function GroupHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="tw:mb-1 tw:px-2 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
      {children}
    </h3>
  );
}
